/**
 * useStaleTaskDetection — auto-close task detail when the task
 * disappears from the current view.
 *
 * Scenarios handled:
 * 1. Task deleted (by another user, API, or sync)
 * 2. Task completed/canceled (by another user or API — NOT by the local user)
 * 3. Task moved to a different container/view
 * 4. Task filtered out (tag filter, text filter, view filter)
 *
 * Pattern used by Linear, Notion, Asana:
 * - Detect that the open item is no longer in scope
 * - Brief delay to avoid flicker during optimistic updates
 * - Close with informative toast
 *
 * The hook runs inside ViewShell and has access to both the active task ID
 * and the final filtered items list.
 *
 * Sync race (MOB-08): a task can be legitimately open (deep link, or a
 * cold-start restore of `?tid=` — see `useNavigationHistory.ts`) before
 * Dexie Cloud's initial sync has pulled it down. `db.totodo.get()` then
 * resolves `undefined` even though the task is real. If the task isn't
 * found locally AND the initial sync hasn't settled yet, this hook waits
 * for sync to settle (bounded — see `MAX_UNSYNCED_WAIT_MS`) before
 * re-checking, instead of immediately closing with a false "Task no
 * longer exists" toast. This only *delays* the stale-close: once sync
 * settles (or the safety timeout elapses) the check always re-runs and a
 * genuinely-gone task is still closed.
 */

import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { db, ObjectStatus } from "@/db/schema"
import type { ViewItem } from "@/features/views/hooks/useViewData"
import { useEditFocusStore } from "../stores/editFocusStore"
import { usePendingCompletions } from "@/features/tasks/stores/pendingCompletions"
import { waitForInitialSync } from "@/shared/utils/sync/waitForInitialSync"

/**
 * Delay before auto-closing (ms). Gives time for:
 * - Optimistic updates to settle
 * - Pending completion to commit (2s) + small buffer
 * - Prevents flash when items list briefly empties during re-query
 */
const STALE_DELAY_MS = 800

/**
 * Safety cap (ms) for waiting on the initial sync to settle before
 * treating a not-found task as confirmed gone (see sync-race note above).
 * `waitForInitialSync` resolves immediately once Dexie Cloud reaches
 * "in-sync"/"error"/"offline" — this cap only guards against sync never
 * emitting at all. Generously covers the addon's own fetch timeout
 * (10s in-browser, see CLAUDE.md's dexie-cloud-addon patches table).
 */
const MAX_UNSYNCED_WAIT_MS = 15_000

/** How long a freshly created task is exempt from stale detection. */
const NEW_TASK_GRACE_MS = 15_000

export function useStaleTaskDetection(
  activeTaskId: string | null,
  items: ViewItem[],
  closeTask: () => void,
  viewId: string,
) {
  const { t } = useTranslation()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closedRef = useRef<string | null>(null)
  // Track tasks we've seen in pending state — these were completed locally
  const locallyCompletedRef = useRef<Set<string>>(new Set())

  // Watch the pending completions store to record locally-initiated completions
  useEffect(() => {
    if (!activeTaskId) return
    const unsub = usePendingCompletions.subscribe((state) => {
      if (state.isPending(activeTaskId)) {
        locallyCompletedRef.current.add(activeTaskId)
      }
    })
    return unsub
  }, [activeTaskId])

  useEffect(() => {
    // Clean up timer when activeTaskId changes (user opened a different task)
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    // No task open — nothing to detect
    if (!activeTaskId) {
      closedRef.current = null
      return
    }

    // Already handled this task (avoid duplicate toasts)
    if (closedRef.current === activeTaskId) return

    // Check if the task is still in the items list
    const isInItems = items.some((item) => item.task.id === activeTaskId)
    if (isInItems) return

    // Don't start stale timer if the task is pending local completion
    const isPending = usePendingCompletions.getState().isPending(activeTaskId)
    if (isPending) return

    // A task created seconds ago is not "moved out of this view" — the view's
    // live query simply has not included it yet. Creating one from the FAB and
    // watching it vanish with that toast was exactly this race: the task was
    // in the list moments later, which is why pressing + again "worked".
    if (
      useEditFocusStore
        .getState()
        .wasRecentlyCreated(activeTaskId, NEW_TASK_GRACE_MS)
    ) {
      return
    }

    // Guards the async chain below against a stale closure firing after
    // the effect has re-run (activeTaskId/items changed) or unmounted —
    // clearTimeout only cancels the *pending* timer, not in-flight awaits.
    let cancelled = false

    // Task not in items — start the stale timer
    timerRef.current = setTimeout(() => {
      timerRef.current = null

      // Double check: still the same active task?
      if (closedRef.current === activeTaskId) return

      void (async () => {
        // Query the task from DB to understand what happened
        let task = await db.totodo.get(activeTaskId)
        if (cancelled) return

        // Not found locally can mean genuinely gone, OR — on a cold-start
        // restore or deep link — that Dexie Cloud's initial sync simply
        // hasn't pulled this task down yet. Wait for sync to settle before
        // treating "not found" as confirmed; re-check afterwards. Already-
        // settled sync (the common case) resolves this immediately.
        if (!task) {
          await waitForInitialSync(db, MAX_UNSYNCED_WAIT_MS)
          if (cancelled) return
          task = await db.totodo.get(activeTaskId)
          if (cancelled) return
        }

        if (closedRef.current === activeTaskId) return

        // Mark as handled to prevent duplicate toasts
        closedRef.current = activeTaskId

        // If this was a locally-initiated completion, just close silently
        // — the user already knows what they did
        const wasLocallyCompleted =
          locallyCompletedRef.current.has(activeTaskId)
        locallyCompletedRef.current.delete(activeTaskId)

        closeTask()

        if (wasLocallyCompleted) return

        if (!task) {
          toast(t("Task no longer exists"))
          return
        }

        if (task.isDeleted === 1) {
          toast(t("Task was deleted"))
          return
        }

        if (task.status === ObjectStatus.Completed) {
          toast(t("Task was completed"))
          return
        }

        if (task.status === ObjectStatus.Canceled) {
          toast(t("Task was canceled"))
          return
        }

        // Task still exists and is active — it was moved or filtered out
        toast(t("Task moved out of this view"))
      })()
    }, STALE_DELAY_MS)

    return () => {
      cancelled = true
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [activeTaskId, items, closeTask, viewId, t])
}
